import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import useUppy from 'modules/common/components/UppyUploader/useUppy';
import STATEMENT_UPLOAD_ANALYTICS_EVENTS from 'services/statementUploadTasks/constants/analytics';

interface UseStatementFileUploadProps {
    accountMask: string;
    plaidInstitutionId: string;
    year: number;
    month: number;
    taskId: string;
    institutionName: string;
}

export default function useStatementFileUpload({
    accountMask,
    plaidInstitutionId,
    year,
    month,
    taskId,
    institutionName,
}: UseStatementFileUploadProps) {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { sendTrackingEvent } = useEventTracking();

    const [isUploading, setIsUploading] = useState(false);

    const prefix = `${year}-${month}-${plaidInstitutionId}-${accountMask}-`;

    const { uppy } = useUppy({
        documentType: 'BUSINESS_BANK_STATEMENT',
        meta: { month, year },
        options: {
            id: `statement-upload-${accountMask}-${year}-${month}`,
            autoProceed: true,
            restrictions: {
                maxNumberOfFiles: 10,
            },
        },
        xhrOptions: {
            fieldName: 'file',
            timeout: 60000,
            limit: 1,
        },
        shouldPreloadData: false,
        onUploadSuccess: async () => {
            sendTrackingEvent(STATEMENT_UPLOAD_ANALYTICS_EVENTS.DOCUMENT_UPLOADED, {
                task_id: taskId,
                institution_name: institutionName,
                accountMask,
                plaidInstitutionId,
                year,
                month,
            });
            try {
                await queryClient.invalidateQueries({ queryKey: ['documents'] });
                enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
            } catch (err) {
                sendDatadogRumError('Failed to invalidate queries after statement upload', err, {
                    accountMask,
                    errorSource: 'useStatementFileUpload.invalidate-queries',
                });
            } finally {
                setIsUploading(false);
            }
        },
        onUploadError: (error: unknown) => {
            setIsUploading(false);
            sendDatadogRumError('Statement file upload error', error, {
                accountMask,
                errorSource: 'useStatementFileUpload.upload-error',
            });
            enqueueSnackbar('Error uploading file', { variant: 'error' });
        },
    });

    const handleUpload = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt';
        input.style.display = 'none';
        document.body.appendChild(input);

        const cleanup = () => {
            if (input.parentNode) {
                input.parentNode.removeChild(input);
            }
        };

        input.onchange = () => {
            if (input.files && input.files.length > 0) {
                setIsUploading(true);

                const hasError = Array.from(input.files).some((file) => {
                    try {
                        const prefixedName = `${prefix}${file.name}`;

                        uppy.addFile({
                            name: prefixedName,
                            type: file.type,
                            data: file,
                            meta: {
                                document_type: 'BUSINESS_BANK_STATEMENT',
                                month,
                                year,
                                originalName: file.name,
                            },
                        });
                        return false;
                    } catch (err) {
                        if (err instanceof Error && err.message.includes('already exists')) {
                            enqueueSnackbar(`File ${file.name} already exists`, { variant: 'warning' });
                        } else {
                            sendDatadogRumError('Failed to add file to statement upload', err, {
                                accountMask,
                                fileName: file.name,
                                errorSource: 'useStatementFileUpload.addFile',
                            });
                            enqueueSnackbar(`Error adding file ${file.name}`, { variant: 'error' });
                        }
                        setIsUploading(false);
                        return true;
                    }
                });

                if (!hasError) {
                    uppy.upload().catch((err) => {
                        setIsUploading(false);
                        sendDatadogRumError('Failed to start statement file upload', err, {
                            accountMask,
                            errorSource: 'useStatementFileUpload.upload',
                        });
                        enqueueSnackbar('Error starting upload', { variant: 'error' });
                    });
                }
            }

            cleanup();
        };

        // Clean up the input element if the user cancels the file dialog
        input.addEventListener('cancel', cleanup);

        input.click();
    }, [uppy, accountMask, prefix, month, year, enqueueSnackbar]);

    return {
        handleUpload,
        isUploading,
    };
}
